import { AudioGraph, type GraphDeps } from './graph.ts';
import { closeMicrophone, openMicrophone } from './microphone.ts';

type BridgeDeps = {
  graph?: GraphDeps;
  open?: () => Promise<MediaStream>;
  close?: (stream: MediaStream) => void;
};

/**
 * A graph with its own microphone, opened at `start` and closed at `stop`:
 * the shape `Recorder` wants, one per attempt in Say it back and Say it with
 * me (media-channel design §4.5). A conversation does not use this; it keeps
 * one `AudioGraph` and leases streams to it through `SessionMicrophone`.
 */
export class AudioBridge {
  private readonly graph: AudioGraph;
  private readonly open: () => Promise<MediaStream>;
  private readonly close: (stream: MediaStream) => void;
  private stream: MediaStream | null = null;
  private stopped = false;

  constructor(onPcm: (pcm: Int16Array) => void, onEnded: () => void, onStarted: () => void, deps: BridgeDeps = {}) {
    this.graph = new AudioGraph({ onPcm, onEnded, onStarted }, deps.graph);
    this.open = deps.open ?? (() => openMicrophone());
    this.close = deps.close ?? ((stream) => closeMicrophone(stream));
  }

  async start(): Promise<void> {
    await this.graph.start();
    // Stopped while the graph loaded: no microphone, and no permission prompt
    // for a card that has already gone.
    if (this.stopped) return;
    const stream = await this.open();
    // Stopped while `getUserMedia` was pending: the stream it hands back is
    // live, and nobody else holds a reference to it.
    if (this.stopped) {
      this.close(stream);
      return;
    }
    this.stream = stream;
    this.graph.attach(stream);
  }

  beginUtterance(): void {
    this.graph.beginUtterance();
  }

  endUtterance(): void {
    this.graph.endUtterance();
  }

  setHandsFree(on: boolean): void {
    this.graph.setHandsFree(on);
  }

  setTutorSpeaking(on: boolean): void {
    this.graph.setTutorSpeaking(on);
  }

  stop(): void {
    this.stopped = true;
    if (this.stream) this.close(this.stream);
    this.stream = null;
    this.graph.stop();
  }
}
